import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  Preference, PREFERENCE_LABELS, RosterRef, peaksFor,
} from '../../../invitations/models/invitation.model';
import { FsComboOption } from '../../../common/fs-combobox/fs-combobox.component';
import { Composition, PoolRow, compositionOf } from '../../services/report-pool';
import { ReportMockService } from '../../services/report-mock.service';
import { REPORT_TYPE_LABELS, ReportType, TEAM_MEMBER_REPORT_CAP } from '../../models/report.model';

@Component({
  selector: 'app-report-step-participants',
  templateUrl: './step-participants.component.html',
  styleUrls: ['./step-participants.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReportStepParticipantsComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() orgs: RosterRef[] = [];

  pool: PoolRow[] = [];
  composition!: Composition;

  search = '';
  view: 'all' | 'selected' = 'all';
  /** Peak chips currently filtering the pool. Empty = no filter. */
  peakFilters: Preference[] = [];
  addingOrg = false;

  readonly preferences: Preference[] = ['cl', 'id', 'dv', 'im'];
  readonly prefLabels = PREFERENCE_LABELS;
  readonly typeLabels = REPORT_TYPE_LABELS;
  readonly cap = TEAM_MEMBER_REPORT_CAP;

  constructor(private svc: ReportMockService) {}

  ngOnInit(): void { this.reloadPool(); }

  // --- source organizations (the chip bar) ---

  get sourceOrgIds(): string[] { return this.form.value.sourceOrgIds as string[]; }
  get selectedIds(): string[] { return this.form.value.participantIds as string[]; }

  sourceOrgs(): RosterRef[] {
    return this.sourceOrgIds
      .map(id => this.orgs.find(o => o.id === id))
      .filter((o): o is RosterRef => !!o);
  }

  /** Orgs not already sourced — the "Add organization" combobox's options. */
  get addableOrgs(): FsComboOption[] {
    return this.orgs.filter(o => !this.sourceOrgIds.includes(o.id)).map(o => ({ id: o.id, name: o.name }));
  }

  /** How many eligible people this org contributes — the chip's badge. */
  orgCount(orgId: string): number { return this.pool.filter(r => r.orgId === orgId).length; }

  addOrg(orgId: string): void {
    if (!orgId || this.sourceOrgIds.includes(orgId)) { return; }
    this.form.get('sourceOrgIds')!.setValue([...this.sourceOrgIds, orgId]);
    this.addingOrg = false;
    this.reloadPool();
  }

  /**
   * Dropping an org also drops its people from the selection — keeping them would leave the report
   * holding participants from an org the picker no longer shows, invisible and unremovable.
   */
  removeOrg(orgId: string): void {
    const goneIds = this.pool.filter(r => r.orgId === orgId).map(r => r.participant.id);
    this.form.get('sourceOrgIds')!.setValue(this.sourceOrgIds.filter(id => id !== orgId));
    this.form.get('participantIds')!.setValue(this.selectedIds.filter(id => !goneIds.includes(id)));
    this.reloadPool();
  }

  private reloadPool(): void {
    this.svc.pool(this.sourceOrgIds).subscribe(rows => {
      this.pool = rows;
      this.recompute();
    });
  }

  private recompute(): void { this.composition = compositionOf(this.pool, this.selectedIds); }

  // --- the list ---

  get rows(): PoolRow[] {
    let rows = this.pool;
    if (this.view === 'selected') { rows = rows.filter(r => this.isSelected(r.participant.id)); }
    if (this.peakFilters.length) {
      rows = rows.filter(r => {
        const pk = peaksFor(r.participant.profileTypeId);
        return this.peakFilters.some(f => pk.includes(f));
      });
    }
    const s = this.search.trim().toLowerCase();
    if (s) {
      rows = rows.filter(r =>
        `${r.participant.firstName} ${r.participant.lastName}`.toLowerCase().includes(s) ||
        r.orgName.toLowerCase().includes(s) ||
        (r.participant.profileType || '').toLowerCase().includes(s));
    }
    return rows;
  }

  isSelected(id: string): boolean { return this.selectedIds.includes(id); }

  toggle(row: PoolRow): void {
    const id = row.participant.id;
    const next = this.isSelected(id)
      ? this.selectedIds.filter(x => x !== id)
      : [...this.selectedIds, id];
    this.form.get('participantIds')!.setValue(next);
    this.recompute();
  }

  /** Add or remove everything currently visible — the filters ARE the bulk selector. */
  toggleAllVisible(): void {
    const visible = this.rows.map(r => r.participant.id);
    const allIn = visible.length > 0 && visible.every(id => this.isSelected(id));
    const next = allIn
      ? this.selectedIds.filter(id => !visible.includes(id))
      : [...this.selectedIds, ...visible.filter(id => !this.isSelected(id))];
    this.form.get('participantIds')!.setValue(next);
    this.recompute();
  }

  allVisibleSelected(): boolean {
    const visible = this.rows;
    return visible.length > 0 && visible.every(r => this.isSelected(r.participant.id));
  }

  peaks(row: PoolRow): Preference[] { return peaksFor(row.participant.profileTypeId); }

  togglePeakFilter(p: Preference): void {
    this.peakFilters = this.peakFilters.includes(p)
      ? this.peakFilters.filter(x => x !== p)
      : [...this.peakFilters, p];
  }

  /** How many people in the pool peak in this preference — the filter chip's badge. */
  poolPeakCount(p: Preference): number {
    return this.pool.filter(r => peaksFor(r.participant.profileTypeId).includes(p)).length;
  }

  clearFilters(): void { this.peakFilters = []; this.search = ''; this.view = 'all'; }

  // --- the rail ---

  /** Bar width as a share of the largest bar, so a small group still reads. 0 when all are 0. */
  barWidth(p: Preference): number {
    const max = Math.max(...this.preferences.map(x => this.composition.peaks[x]));
    return max ? (this.composition.peaks[p] / max) * 100 : 0;
  }

  get missingLabels(): string { return this.composition.missing.map(p => this.prefLabels[p]).join(', '); }

  /**
   * The roster caps the Team Member Report at 20. Known issue — the product decision is to warn
   * and allow, so this never blocks Next.
   */
  get overCap(): boolean {
    return (this.form.value.type as ReportType) === 'team_member_report' && this.composition.count > this.cap;
  }
}
